import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, idr } from "../lib/api";
import {
  Plus, Trash2, X, Download, Send, Sparkles, CheckCircle2, AlertTriangle,
  ShoppingCart, Info, Split, Pencil, FileText, ArrowDownRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AutocompleteInput from "../components/hris/AutocompleteInput";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniISO } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const LEADS = ["Walk In", "Chat WA", "Chat IG", "Chat TikTok", "Online Shopping", "Affiliate", "Referral", "Reseller", "Lainnya"];

// Nomor tampilan kosmetik untuk sale yang belum punya sistem penomoran
// formal — SAMA PERSIS `_no_faktur()` di backend/routers/faktur.py (dipakai
// di faktur/struk cetak), diduplikasi di sini (bukan diimpor dari satu
// tempat) karena berkas yang boleh disentuh untuk brief ini tidak termasuk
// `lib/api.js` atau berkas bersama lain. Turunan dari id, bukan disimpan.
const noInvoice = (id) => `INV-${(id || "").slice(0, 8).toUpperCase()}`;

const emptyForm = () => ({
  id: null,
  tanggal: hariIniISO(),
  nama_barang: "",
  imei: "",
  jumlah: 0,
  mode: "single",           // 'single' | 'split'
  // "Cash" — nama bawaan metode pembayaran (koleksi payment_methods), BUKAN
  // lagi "Tunai" — sejak fondasi channel pindah ke metode pembayaran, "Tunai"
  // tidak lagi ada di daftar aktif (lihat channelOptions di bawah untuk
  // kenapa dropdown tidak akan pernah tampak kosong walau default ini
  // berbeda dari daftar terkini toko).
  single_channel: "Cash",
  payments: [],             // used only in split mode
  keterangan: "",
  sales: "",
  nama_pelanggan: "",
  leads: "Walk In",
  barang_akuisisi: "",
  harga_akuisisi: 0,
  peng_akuisisi: "",
  branch: "",
});

export default function RekonSales() {
  const { user, can } = useAuth();
  const bolehLihatSales = can("sales", "view");
  const BRANCHES = useCabangOptions({ denganKosong: true });
  // Cermin persis `role_atau_izin()` di backend/deps.py — superadmin/admin
  // selalu lolos, sisanya ikut matriks izin. KENAPA cabang role tetap ada:
  // endpointnya (`require_role_atau_izin`) juga meloloskan mereka tanpa melihat
  // matriks, jadi tanpa baris ini seorang admin yang baris rekon_sales-nya
  // sengaja dikosongkan akan kehilangan tombol untuk aksi yang sebenarnya
  // masih boleh dia lakukan.
  const rolIstimewa = ["superadmin", "admin"].includes(user?.role);
  // Dulu `user?.role !== "viewer"` — praktis selalu true, jadi tombol Edit &
  // Hapus tampil untuk SEMUA orang lalu berujung 403. Sekarang tiap tombol
  // memakai kunci + aksi yang SAMA PERSIS dengan gerbang endpoint-nya:
  //   Edit   → PATCH /rekon-sales/{id}   = rekon_sales.update (+ role istimewa)
  //   Hapus  → DELETE /rekon-sales/{id}  = rekon_sales.delete MURNI
  //            (require_permission, tanpa jalan pintas role) — makanya TIDAK
  //            memakai `rolIstimewa`: preset Kasir memberi delete=False dan
  //            memang harus begitu.
  //   Export → tiga tombol, TIGA gerbang berbeda; lihat blok di bawahnya.
  const canEdit = rolIstimewa || can("rekon_sales", "update");
  const canCreate = rolIstimewa || can("rekon_sales", "create");
  const canDelete = can("rekon_sales", "delete");
  // Halaman ini punya TIGA tombol export yang memanggil TIGA endpoint dengan
  // gerbang yang TIDAK sama, jadi satu flag `canExport` untuk ketiganya pasti
  // meleset di salah satunya — dan memang meleset: satu flag dipakai untuk dua
  // tombol sekaligus, sehingga admin yang baris `rekon_sales`-nya sengaja
  // dicentang-mati tetap melihat tombol "Export Bulanan" lalu kena 403
  // (endpoint itu `require_permission` MURNI, tanpa jalan pintas role), dan
  // tombol export di panel COA malah tidak digerbangi sama sekali.
  // Sekarang satu flag per endpoint, tiap flag cermin persis gerbang backend-nya:
  //   GET /rekon-sales/export/xlsx   rekon.py    require_role_atau_izin(
  //       rekon_sales, view, superadmin, admin)          -> rolIstimewa || can
  //   GET /export/rekon-sales-daily  exports.py  require_permission(
  //       rekon_sales, view) — izin MURNI                -> can() saja
  //   GET /coa-recon/sales/export    rekon.py    require_role_atau_izin(
  //       rekon_sales, view, superadmin, admin, kasir_pos)
  // `kasir_pos` ikut disebut supaya cerminnya utuh; praktis tidak terjangkau
  // karena panel COA (`GET /coa-recon/sales`, izin murni) tidak akan muncul
  // untuk akun yang cuma punya role itu — tapi menghilangkannya dari sini akan
  // menyembunyikan tombol dari orang yang endpoint-nya masih meloloskan.
  const canExportHarian = rolIstimewa || can("rekon_sales", "view");
  const canExportBulanan = can("rekon_sales", "view");
  const canExportCoa = canExportHarian || user?.role === "kasir_pos";
  const canAdjust = ["superadmin", "admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [channels, setChannels] = useState([]);
  const [date, setDate] = useState(hariIniISO());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branch, setBranch] = useState("");
  const [modal, setModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [coa, setCoa] = useState(null);
  const [coaDraft, setCoaDraft] = useState({});
  const [adjDraft, setAdjDraft] = useState({});
  const [adjNoteDraft, setAdjNoteDraft] = useState({});
  const [txActualDraft, setTxActualDraft] = useState({});
  const [txNoteDraft, setTxNoteDraft] = useState({});
  // Rincian penerimaan aktual PER AKUN (brief bos 14 Agu #1) — dipakai saat
  // pembayaran campuran (>1 channel) dan/atau ada trade-in.
  // chActualDraft: { [tx_key]: { [channel]: string } }
  // tiActualDraft: { [tx_key]: string } — nilai penerimaan trade-in aktual.
  const [chActualDraft, setChActualDraft] = useState({});
  const [tiActualDraft, setTiActualDraft] = useState({});
  // Detail popup per transaksi (poin D brief bos) — dibuka dengan klik baris.
  // Tidak butuh GET tambahan: baris di `items` sudah membawa semua field yang
  // dibutuhkan popup (payments/breakdown/actual_value/dst.), beda dari pola
  // Acquisitions.jsx yang detailnya memang tidak lengkap di listing.
  const [detail, setDetail] = useState(null);
  // Jurnal penyesuaian selisih bulanan (poin C brief bos)
  const [adjStatus, setAdjStatus] = useState(null);
  const [adjBusy, setAdjBusy] = useState(false);
  const isRange = !!(dateFrom || dateTo);
  // Channel AKTIF saat ini (metode pembayaran) — dipakai menandai baris/opsi
  // yang channel-nya sudah tidak aktif/nama lama ("channel lama").
  const channelSet = useMemo(() => new Set(channels), [channels]);
  // Dropdown form HARUS tetap menampilkan channel milik baris yang sedang
  // diedit walau channel itu sudah tidak aktif — kalau tidak, <select> React
  // akan terlihat kosong/salah pilih (value-nya tidak match opsi manapun)
  // padahal state form masih menyimpan nilai lama itu dengan benar.
  const channelOptions = useMemo(() => {
    const extra = new Set();
    (form.payments || []).forEach(p => p.channel && extra.add(p.channel));
    if (form.single_channel) extra.add(form.single_channel);
    channels.forEach(c => extra.delete(c));
    return [...channels, ...[...extra].sort()];
  }, [channels, form.payments, form.single_channel]);
  const periodeBulan = date.slice(0, 7);
  // Nomor urut request: mengisi "Dari"/"Sampai" berarti dua kali ganti filter
  // beruntun, jadi respons rentang lama bisa datang belakangan dan menimpa angka
  // rentang aktif. Hanya respons request TERAKHIR yang boleh menyentuh state.
  const reqSeq = useRef(0);
  const load = () => {
    const seq = ++reqSeq.current;
    const p = new URLSearchParams();
    if (isRange) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    } else {
      p.set("date", date);
    }
    if (branch) p.set("branch", branch);
    api.get(`/rekon-sales?${p}`).then(r => {
      if (seq !== reqSeq.current) return;   // respons basi — buang
      setItems(r.data);
      const d = {}, n = {}, chd = {}, tid = {};
      (r.data || []).forEach(x => {
        if (x.actual_value != null) d[x.tx_key] = x.actual_value;
        if (x.actual_note) n[x.tx_key] = x.actual_note;
        if (x.channel_actuals) {
          chd[x.tx_key] = {};
          x.channel_actuals.forEach(c => { chd[x.tx_key][c.channel] = c.actual_value; });
        }
        if (x.trade_in_actual != null) tid[x.tx_key] = x.trade_in_actual;
      });
      setTxActualDraft(d);
      setTxNoteDraft(n);
      setChActualDraft(chd);
      setTiActualDraft(tid);
    }).catch(e => {
      // Tanpa .catch, gagal muat = unhandled rejection: tabel tetap
      // memperlihatkan baris tanggal SEBELUMNYA tanpa satu pun tanda bahwa
      // filter yang baru dipilih tidak pernah termuat. Toast-nya hanya di
      // pemanggil ini (bukan di ketiganya) supaya satu kali koneksi putus
      // tidak melahirkan tiga toast sekaligus.
      if (seq !== reqSeq.current) return;
      setItems([]);
      toast.error(e?.response?.data?.detail || "Gagal memuat data rekon");
    });
    api.get(`/rekon-sales/summary?${p}`).then(r => { if (seq === reqSeq.current) setSummary(r.data); })
      // Diam (sudah ada toast dari pemanggil di atas), tapi ringkasannya
      // DIKOSONGKAN — angka lama yang menempel di layar lebih menyesatkan
      // daripada panel yang hilang.
      .catch(() => { if (seq === reqSeq.current) setSummary(null); });
    if (!isRange) {
      api.get(`/coa-recon/sales?${p}`).then(r => {
        if (seq !== reqSeq.current) return;
        setCoa(r.data);
        const d = {}, adj = {}, adjN = {};
        (r.data.rows || []).forEach(x => {
          d[x.channel] = x.actual;
          adj[x.channel] = x.adjustment || 0;
          adjN[x.channel] = x.adjustment_note || "";
        });
        setCoaDraft(d); setAdjDraft(adj); setAdjNoteDraft(adjN);
      }).catch(() => {   // alasan sama dengan .catch ringkasan di atas
        if (seq !== reqSeq.current) return;
        setCoa(null); setCoaDraft({}); setAdjDraft({}); setAdjNoteDraft({});
      });
    } else {
      setCoa(null); setCoaDraft({}); setAdjDraft({}); setAdjNoteDraft({});
    }
  };
  const saveCoaActual = async (channel) => {
    try {
      await api.post("/rekon-actuals", {
        date, branch, type: "sales", channel,
        actual_amount: parseFloat(coaDraft[channel]) || 0,
        adjustment_amount: parseFloat(adjDraft[channel]) || 0,
        adjustment_note: adjNoteDraft[channel] || "",
      });
      toast.success(`${channel} tersimpan`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const saveTxActual = async (txKey) => {
    try {
      const nilai = parseFloat(txActualDraft[txKey]) || 0;
      const note = txNoteDraft[txKey] || "";
      await api.post("/rekon-tx-actuals", { tx_key: txKey, actual_value: nilai, note });
      toast.success("Nilai aktual tersimpan");
      // Popup detail memegang salinan baris sendiri (bukan turunan langsung
      // dari `items`) — sinkronkan optimistis supaya angka & selisih_tx di
      // popup ikut ter-update tanpa menunggu round-trip `load()` berikutnya.
      setDetail(d => (d && d.tx_key === txKey)
        ? { ...d, actual_value: nilai, actual_note: note, selisih_tx: round2(nilai - (d.jumlah || 0)) }
        : d);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const round2 = (n) => Math.round(n * 100) / 100;

  const openDetail = (it) => {
    setDetail(it);
    setTxActualDraft(d => (d[it.tx_key] != null ? d : { ...d, [it.tx_key]: it.actual_value ?? "" }));
    setTxNoteDraft(n => (n[it.tx_key] != null ? n : { ...n, [it.tx_key]: it.actual_note || "" }));
    setChActualDraft(d => (d[it.tx_key] ? d : { ...d, [it.tx_key]: {} }));
    setTiActualDraft(d => (d[it.tx_key] != null ? d : { ...d, [it.tx_key]: "" }));
  };

  // Simpan rincian penerimaan aktual PER AKUN + trade-in (brief bos 14 Agu
  // #1) — dipakai saat pembayaran campuran (>1 channel) dan/atau ada
  // trade-in. `channel_actuals` SELALU dikirim untuk SETIAP channel milik
  // baris ini (termasuk yang belum diisi -> 0) supaya total di server persis
  // mencerminkan apa yang terlihat di popup, bukan cuma channel yang disentuh.
  const saveTxActualBreakdown = async (item) => {
    try {
      const chDraft = chActualDraft[item.tx_key] || {};
      const channel_actuals = Object.keys(item.breakdown || {}).map(ch => ({
        channel: ch, actual_value: parseFloat(chDraft[ch]) || 0,
      }));
      const trade_in_actual = parseFloat(tiActualDraft[item.tx_key]) || 0;
      const note = txNoteDraft[item.tx_key] || "";
      const { data } = await api.post("/rekon-tx-actuals", {
        tx_key: item.tx_key, channel_actuals, trade_in_actual, note,
      });
      toast.success("Rincian penerimaan tersimpan");
      setDetail(d => (d && d.tx_key === item.tx_key) ? {
        ...d,
        actual_value: data.actual_value,
        channel_actuals: data.channel_actuals,
        trade_in_actual: data.trade_in_actual,
        selisih_tx: round2(data.actual_value - (d.jumlah_penerimaan_ekspektasi ?? d.jumlah ?? 0)),
      } : d);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const exportCoaExcel = async () => {
    const token = localStorage.getItem("hris_token");
    const p = new URLSearchParams({ date });
    if (branch) p.set("branch", branch);
    // Disamakan dengan `exportExcel`/`exportDaily`: fetch() yang gagal (koneksi
    // putus / server mati) MELEMPAR, dan tanpa try ini jadi unhandled rejection
    // — tombolnya terlihat tidak melakukan apa pun dan tidak ada jejak apa pun
    // di layar. Pesan 403-nya juga dibedakan supaya staf tahu ini soal izin,
    // bukan berkas rusak. (`pesanGagalExport` didefinisikan lebih jauh di bawah;
    // dipanggil saat klik, jadi sudah pasti terbentuk.)
    try {
      const res = await fetch(`${API}/coa-recon/sales/export?${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error(pesanGagalExport(res.status, "Rekap COA")); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `COA_RekonSales_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export — koneksi bermasalah"); }
  };
  // dateFrom/dateTo WAJIB ikut jadi dependency: `load` membaca keduanya lewat
  // `isRange`, jadi tanpa ini mengisi filter rentang tidak memicu fetch ulang dan
  // angka yang tampil masih milik tanggal tunggal sebelumnya.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [date, dateFrom, dateTo, branch]);
  useEffect(() => {
    // Dua fetch pengisi dropdown. Kalau gagal, dropdown-nya kosong — itu
    // sudah terlihat sendiri di layar, jadi tidak perlu toast; yang tidak
    // boleh adalah unhandled rejection tanpa jejak apa pun.
    api.get("/rekon-sales/channels").then(r => setChannels(r.data.channels)).catch(() => setChannels([]));
    api.get("/employees").then(r => setEmployees((r.data || [])
      .filter(e => e.is_active !== false)
      .sort((a, b) => a.nama.localeCompare(b.nama)))).catch(() => setEmployees([]));
  }, []);

  // Status jurnal penyesuaian bulanan — dimuat ulang tiap ganti bulan/branch
  // (bukan admin: tidak perlu memuat sama sekali, tombolnya juga tersembunyi).
  const loadAdjStatus = () => {
    if (!canAdjust) { setAdjStatus(null); return; }
    api.get("/coa-recon/sales/adjustment-journal/status", { params: { periode: periodeBulan, branch } })
      .then(r => setAdjStatus(r.data)).catch(() => setAdjStatus(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAdjStatus(); }, [periodeBulan, branch, canAdjust]);

  const postAdjustment = async () => {
    if (!confirm(`Posting jurnal penyesuaian selisih rekon sales untuk periode ${periodeBulan}`
        + (branch ? ` · ${branch}` : " · semua cabang") + "?")) return;
    setAdjBusy(true);
    try {
      const { data } = await api.post("/coa-recon/sales/adjustment-journal", { periode: periodeBulan, branch });
      toast.success(data.message || "Diproses");
      loadAdjStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setAdjBusy(false); }
  };
  const voidAdjustment = async () => {
    const alasan = prompt("Alasan pembatalan jurnal penyesuaian:");
    if (!alasan || !alasan.trim()) return;
    setAdjBusy(true);
    try {
      const { data } = await api.post("/coa-recon/sales/adjustment-journal/void",
        { periode: periodeBulan, branch, alasan: alasan.trim() });
      toast.success(data.message || "Dibatalkan");
      loadAdjStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setAdjBusy(false); }
  };

  const splitBalance = useMemo(() => {
    const sum = (form.payments || []).reduce((a, p) => a + (parseFloat(p.amount) || 0), 0);
    return (parseFloat(form.jumlah) || 0) - sum;
  }, [form.jumlah, form.payments]);
  const splitTotal = useMemo(() =>
    (form.payments || []).reduce((a, p) => a + (parseFloat(p.amount) || 0), 0),
    [form.payments]);

  const openCreate = () => {
    setMode("create");
    setForm({ ...emptyForm(), tanggal: date, branch });
    setModal(true);
  };

  const openEdit = (r) => {
    setMode("edit");
    setForm({
      id: r.id,
      tanggal: r.tanggal, nama_barang: r.nama_barang, imei: r.imei || "",
      jumlah: r.jumlah,
      mode: (r.payments && r.payments.length > 1) ? "split" : "single",
      single_channel: r.payments?.[0]?.channel || "Cash",
      payments: r.payments || [],
      keterangan: r.keterangan || "",
      sales: r.sales || "", nama_pelanggan: r.nama_pelanggan || "",
      leads: r.leads || "Walk In",
      barang_akuisisi: r.barang_akuisisi || "",
      harga_akuisisi: r.harga_akuisisi || 0,
      peng_akuisisi: r.peng_akuisisi || "",
      branch: r.branch || "",
    });
    setModal(true);
  };

  const save = async () => {
    if (!form.nama_barang) { toast.error("Nama barang wajib"); return; }
    if (!form.jumlah || form.jumlah <= 0) { toast.error("Jumlah harus > 0"); return; }
    let payments = [];
    if (form.mode === "single") {
      payments = [{ channel: form.single_channel, amount: parseFloat(form.jumlah) }];
    } else {
      payments = (form.payments || []).filter(p => p.channel && p.amount > 0);
      if (payments.length === 0) { toast.error("Isi minimal 1 baris pembayaran"); return; }
    }
    const payload = {
      tanggal: form.tanggal, nama_barang: form.nama_barang, imei: form.imei,
      jumlah: parseFloat(form.jumlah), payments,
      keterangan: form.keterangan, sales: form.sales,
      nama_pelanggan: form.nama_pelanggan, leads: form.leads,
      barang_akuisisi: form.barang_akuisisi,
      harga_akuisisi: parseFloat(form.harga_akuisisi) || 0,
      peng_akuisisi: form.peng_akuisisi, branch: form.branch,
    };
    setSaving(true);
    try {
      if (mode === "edit" && form.id) {
        const { data } = await api.patch(`/rekon-sales/${form.id}`, payload);
        toast.success(data.status === "CEK" ? `Tersimpan tapi ⚠️ CEK (selisih ${idr(data.selisih)})` : "Tersimpan · VALID");
      } else {
        const { data } = await api.post("/rekon-sales", payload);
        toast.success(data.status === "CEK" ? `Tersimpan tapi ⚠️ CEK (selisih ${idr(data.selisih)})` : "Tersimpan · VALID");
      }
      setModal(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  // Modal input/edit transaksi rekon menyentuh nominal uang (jumlah, split
  // pembayaran, harga akuisisi) — JSX-nya ada di komponen halaman yang tidak
  // unmount, jadi `active` mengikuti `modal` supaya dirty-state di-reset
  // tiap kali dibuka lagi untuk transaksi lain.
  const rekonGuard = useModalGuard({
    active: modal, dangerous: true, busy: saving,
    onClose: () => setModal(false), onSubmit: save,
  });
  // Urutan bawaan dipertahankan sampai header diklik. Kolom "#" tetap
  // penomoran baris tampilan (bukan makna urutan), jadi aman ikut sort ulang.
  const { sortKey: rekonSortKey, sortDir: rekonSortDir, toggle: toggleRekonSort } = useSortState();

  const del = async (id) => {
    if (!confirm("Hapus transaksi ini?")) return;
    // Tanpa try/catch, 403 (atau koneksi putus) hanya melahirkan unhandled
    // rejection: tidak ada toast, tidak ada apa pun — baris tetap di layar dan
    // pengguna menyangka aplikasinya menggantung.
    try {
      await api.delete(`/rekon-sales/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || (e?.response?.status === 403 ? "Tidak punya izin menghapus rekon" : "Gagal menghapus"));
      return;
    }
    toast.success("Transaksi dihapus");
    setDetail(d => (d && d.id === id) ? null : d);   // tutup popup kalau baris yang dihapus sedang dibuka
    load();
  };

  // Pesan gagal export dibedakan 403 vs sisanya: "Gagal export" saja membuat
  // staf mengira filenya rusak, padahal masalahnya izin — dan tidak ada tempat
  // lain di layar ini yang memberitahunya.
  const pesanGagalExport = (status, apa = "Rekon Sales") =>
    status === 403 ? `Tidak punya izin export ${apa}` : "Gagal export";

  const exportExcel = async () => {
    const token = localStorage.getItem("hris_token");
    const p = new URLSearchParams();
    p.set("date", date);
    if (branch) p.set("branch", branch);
    // fetch() yang gagal (koneksi putus / server mati) melempar — tanpa try
    // ini jadi unhandled rejection dan tombolnya terlihat tidak melakukan apa pun.
    try {
      const res = await fetch(`${API}/rekon-sales/export/xlsx?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error(pesanGagalExport(res.status)); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `RekonSales_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export — koneksi bermasalah"); }
  };

  const exportDaily = async () => {
    const token = localStorage.getItem("hris_token");
    const periode = date.slice(0, 7);
    const p = new URLSearchParams({ periode });
    if (branch) p.set("branch", branch);
    try {
      const res = await fetch(`${API}/export/rekon-sales-daily?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error(pesanGagalExport(res.status)); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `RekonSales_Daily_${periode}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export per-hari (31 sheet) sedang diunduh");
    } catch { toast.error("Gagal export — koneksi bermasalah"); }
  };

  const addPay = () => {
    const usedChannels = new Set((form.payments || []).map(p => p.channel));
    const nextCh = channels.find(c => !usedChannels.has(c)) || channels[0];
    setForm(s => ({ ...s, payments: [...(s.payments || []),
       { channel: nextCh, amount: Math.max(0, splitBalance) }] }));
  };
  const updPay = (i, k, v) => setForm(s => {
    const np = [...(s.payments || [])];
    np[i] = { ...np[i], [k]: k === "amount" ? (parseFloat(v) || 0) : v };
    return { ...s, payments: np };
  });
  const rmPay = (i) => setForm(s => ({ ...s, payments: (s.payments || []).filter((_, x) => x !== i) }));

  return (
    <div className="p-4 md:p-8 max-w-[1600px]">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Rekon Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Rekonsiliasi Harian</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-3xl">
            <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1 text-slate-400" />
            Setiap baris = 1 transaksi. Pilih <b>Sekali Bayar</b> jika 1 metode, atau <b>Campuran/Split</b> jika lebih.
            Selisih & status <b>VALID/CEK</b> dihitung otomatis. Baris dengan badge <b className="text-sky-700">Auto</b> otomatis muncul dari nota Sales yang sudah disetujui (read-only).
          </p>
        </div>
        <div className="flex gap-2">
          {canExportHarian && (
            <button onClick={exportExcel} data-testid="btn-export-rekon"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium">
              <Download className="w-4 h-4" /> Excel Harian
            </button>
          )}
          {canExportBulanan && (
            <button onClick={exportDaily} data-testid="btn-export-rekon-daily"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium">
              <Download className="w-4 h-4" /> Export Bulanan (Per Sheet)
            </button>
          )}
          {canCreate && (
            <button onClick={openCreate} data-testid="btn-add-rekon"
              className="hidden">+</button>
          )}
        </div>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4 flex flex-wrap items-end gap-3">
        <label>
          <span className="text-xs font-medium text-slate-600">Tanggal</span>
          <DatePicker value={date} onChange={v => { setDate(v); setDateFrom(""); setDateTo(""); }} data-testid="rekon-date"
            className="block mt-1" />
        </label>
        <label>
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom} data-testid="rekon-date-from"
            className="block mt-1" />
        </label>
        <label>
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo} data-testid="rekon-date-to"
            className="block mt-1" />
        </label>
        {isRange && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="rekon-reset-range"
            className="h-10 px-3 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
            Reset Range
          </button>
        )}
        <label>
          <span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            className="block mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
        <div className="ml-auto grid grid-cols-3 md:grid-cols-5 gap-3 text-right">
          <div>
            <p className="text-xs text-slate-500">Transaksi</p>
            <p className="font-display text-xl md:text-2xl font-bold text-slate-900" data-testid="stat-tx">{summary?.n_tx ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Omzet</p>
            <p className="font-display text-xl md:text-2xl font-bold text-emerald-700">{idr(summary?.total_omzet ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Selisih Split</p>
            <p className={`font-display text-xl md:text-2xl font-bold ${Math.abs(summary?.total_selisih ?? 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>{idr(summary?.total_selisih ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Net Selisih COA</p>
            <p className={`font-display text-xl md:text-2xl font-bold ${Math.abs(coa?.total_net_selisih ?? 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`} data-testid="stat-net-selisih">{idr(coa?.total_net_selisih ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Butuh CEK</p>
            <p className={`font-display text-xl md:text-2xl font-bold ${(summary?.n_cek ?? 0) > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="stat-cek">{summary?.n_cek ?? 0}</p>
          </div>
        </div>
      </div>

      {/* COA Recon: real receipts vs expected */}
      {coa && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-4" data-testid="coa-panel">
          <div className="px-4 py-3 border-b border-slate-100 bg-emerald-50/40 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <ArrowDownRight className="w-4 h-4 text-emerald-700" />
              <p className="text-sm font-semibold text-slate-900">Rekap COA Penerimaan — Ekspektasi vs Aktual</p>
            </div>
            {/* Dulu dirender TANPA syarat apa pun: begitu `coa` terisi tombol
                muncul — yaitu untuk siapa pun yang punya `rekon_sales.view`,
                karena itulah gerbang panel ini. Endpoint-nya sementara itu
                cuma menerima role, jadi kasir menekannya dan dapat 403 di
                halaman yang dua tombol export-nya justru baru dibuka: dua
                jalan, yang ketiga gagal — terbaca seperti berkasnya rusak,
                bukan seperti izin. Gerbang endpoint sudah disamakan ke
                `rekon_sales.view`; flag ini cerminnya. */}
            {canExportCoa && (
              <button onClick={exportCoaExcel} data-testid="btn-export-coa"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                <Download className="w-3.5 h-3.5" /> Export Excel
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs uppercase font-semibold text-slate-500">
                  <th className="text-left py-2 px-3">Channel (COA)</th>
                  <th className="text-right py-2 px-3">Ekspektasi</th>
                  <th className="text-right py-2 px-3">Aktual Diterima</th>
                  <th className="text-right py-2 px-3">Selisih</th>
                  <th className="text-right py-2 px-3" title="Penyesuaian manual atas selisih (mis. biaya admin bank, potongan MDR)">Penyesuaian</th>
                  <th className="text-right py-2 px-3">Net Selisih</th>
                  <th className="text-center py-2 px-3">Status</th>
                  {canAdjust && <th className="text-center py-2 px-3">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {(coa.rows || []).filter(r => r.expected > 0 || r.actual > 0 || (r.adjustment || 0) !== 0).length === 0 && (
                  <tr><td colSpan={canAdjust ? 8 : 7} className="text-center py-6 text-slate-400 text-xs">Belum ada transaksi untuk {date}</td></tr>
                )}
                {(coa.rows || []).filter(r => r.expected > 0 || r.actual > 0 || (r.adjustment || 0) !== 0).map(r => (
                  <tr key={r.channel} className="border-b border-slate-100" data-testid={`coa-row-${r.channel}`}>
                    <td className="py-2 px-3 font-medium">
                      {r.channel}
                      {!channelSet.has(r.channel) && (
                        <span title="Channel ini bukan metode pembayaran aktif saat ini — kemungkinan data lama"
                          className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 align-middle">channel lama</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{idr(r.expected)}</td>
                    <td className="py-2 px-3 text-right">
                      {canAdjust ? (
                        <InputRupiah value={coaDraft[r.channel] ?? ""}
                          onChange={n => setCoaDraft(d => ({ ...d, [r.channel]: n }))}
                          testid={`coa-actual-${r.channel}`}
                          className="h-8 px-2 rounded border border-slate-200 text-xs text-right font-mono w-32" />
                      ) : <span className="font-mono">{idr(r.actual)}</span>}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${Math.abs(r.selisih) < 0.5 ? "text-slate-500" : "text-rose-700 font-semibold"}`}>
                      {r.selisih === 0 ? "-" : idr(r.selisih)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {canAdjust ? (
                        <div className="flex flex-col items-end gap-1">
                          <input type="number" value={adjDraft[r.channel] ?? ""}
                            onChange={e => setAdjDraft(d => ({ ...d, [r.channel]: e.target.value }))}
                            data-testid={`coa-adj-${r.channel}`}
                            placeholder="0"
                            className="h-8 px-2 rounded border border-amber-200 bg-amber-50/40 text-xs text-right font-mono w-28" />
                          <input type="text" value={adjNoteDraft[r.channel] ?? ""}
                            onChange={e => setAdjNoteDraft(d => ({ ...d, [r.channel]: e.target.value }))}
                            data-testid={`coa-adj-note-${r.channel}`}
                            placeholder="ket. penyesuaian"
                            className="h-7 px-2 rounded border border-slate-200 text-[10px] text-right w-28 italic text-slate-500" />
                        </div>
                      ) : <span className="font-mono">{idr(r.adjustment || 0)}</span>}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-bold ${Math.abs(r.net_selisih || 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>
                      {(r.net_selisih || 0) === 0 ? "-" : idr(r.net_selisih || 0)}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {r.status === "VALID" ? (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> VALID
                        </span>
                      ) : r.status === "CEK" ? (
                        <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-xs font-bold inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> CEK
                        </span>
                      ) : <span className="text-xs text-slate-400">-</span>}
                    </td>
                    {canAdjust && (
                      <td className="py-2 px-3 text-center">
                        <button onClick={() => saveCoaActual(r.channel)}
                          data-testid={`btn-save-coa-${r.channel}`}
                          className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium">Simpan</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {coa.total_expected > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="py-2 px-3 font-bold text-slate-700">TOTAL</td>
                    <td className="py-2 px-3 text-right font-mono font-bold">{idr(coa.total_expected)}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold">{idr(coa.total_actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-bold ${Math.abs(coa.total_selisih) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>{idr(coa.total_selisih)}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold text-amber-700">{idr(coa.total_adjustment || 0)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-bold ${Math.abs(coa.total_net_selisih || 0) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>{idr(coa.total_net_selisih || 0)}</td>
                    <td colSpan={canAdjust ? 2 : 1}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Jurnal Penyesuaian Selisih Rekon — bulanan, brief bos poin 5 */}
      {canAdjust && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4" data-testid="rekon-adjustment-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Jurnal Penyesuaian Selisih — {periodeBulan}{branch ? ` · ${branch}` : " · Semua Cabang"}
              </p>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                Menjurnal selisih Aktual vs Ekspektasi SEBULAN PENUH: kekurangan → Beban Admin
                Bank/MDR, kelebihan → Pendapatan Lain-lain. Channel kas tunai & channel yang
                tidak bisa dipetakan ke akun bank dilewati otomatis (butuh review manual).
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {adjStatus?.status === "posted" ? (
                <>
                  <span className="text-xs px-2.5 py-1.5 rounded-full bg-emerald-100 text-emerald-800 font-medium"
                    data-testid="rekon-adjustment-posted-badge">
                    Sudah diposting · {idr(adjStatus.net_total || 0)}
                  </span>
                  <button onClick={voidAdjustment} disabled={adjBusy} data-testid="btn-void-rekon-adjustment"
                    className="h-9 px-3 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-medium disabled:opacity-50">
                    Batalkan
                  </button>
                </>
              ) : (
                <button onClick={postAdjustment} disabled={adjBusy} data-testid="btn-post-rekon-adjustment"
                  className="h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium disabled:opacity-50">
                  {adjBusy ? "Memproses..." : "Posting Jurnal Penyesuaian"}
                </button>
              )}
            </div>
          </div>
          {adjStatus?.status === "posted" && (adjStatus.lines || []).length > 0 && (
            <div className="mt-3 text-xs text-slate-600 space-y-1">
              {adjStatus.lines.map((l, i) => (
                <p key={i} className="font-mono">
                  {l.channel}: {l.arah === "kekurangan" ? "−" : "+"}{idr(l.amount)}
                  {" · "}{l.arah === "kekurangan" ? l.akun_beban_nama : l.akun_pendapatan_nama}
                </p>
              ))}
            </div>
          )}
          {(adjStatus?.dilewati || []).length > 0 && (
            <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2"
              data-testid="rekon-adjustment-dilewati">
              {adjStatus.dilewati.length} channel dilewati (butuh review manual):{" "}
              {adjStatus.dilewati.map(d => `${d.channel} (${idr(d.net_selisih)})`).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Channel breakdown */}
      {summary?.by_channel?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Breakdown per Channel — {date}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {summary.by_channel.map(c => (
              <div key={c.channel} className="border border-slate-100 rounded-lg p-2 bg-slate-50/40">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{c.channel}</p>
                <p className="font-mono font-semibold text-sm text-slate-900">{idr(c.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="text-left py-3 px-3">#</th>
              <SortTh label="Barang" sortKeyName="nama_barang" sortKey={rekonSortKey} sortDir={rekonSortDir} onSort={toggleRekonSort} testid="rekon-sort-barang" />
              <SortTh label="Jumlah" sortKeyName="jumlah" sortKey={rekonSortKey} sortDir={rekonSortDir} onSort={toggleRekonSort} align="right" testid="rekon-sort-jumlah" />
              <SortTh label="Nilai Aktual" sortKeyName="actual_value" sortKey={rekonSortKey} sortDir={rekonSortDir} onSort={toggleRekonSort} align="right" testid="rekon-sort-aktual" />
              <SortTh label="Selisih Tx" sortKeyName="selisih_tx" sortKey={rekonSortKey} sortDir={rekonSortDir} onSort={toggleRekonSort} align="right" testid="rekon-sort-selisih" />
              <th className="text-left py-3 px-3">Metode</th>
              <SortTh label="Status" sortKeyName="status" sortKey={rekonSortKey} sortDir={rekonSortDir} onSort={toggleRekonSort} testid="rekon-sort-status" />
              <SortTh label="Sales" sortKeyName="sales" sortKey={rekonSortKey} sortDir={rekonSortDir} onSort={toggleRekonSort} testid="rekon-sort-sales" />
              <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={rekonSortKey} sortDir={rekonSortDir} onSort={toggleRekonSort} testid="rekon-sort-pelanggan" />
              <th className="text-left py-3 px-3">Trade-in</th>
              <th className="text-right py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {sortRows(items, rekonSortKey, rekonSortDir).map((it, idx) => {
              const methods = it.payments?.length > 1 ? `Campuran (${it.payments.length})` : (it.payments?.[0]?.channel || "-");
              const adaChannelLama = (it.payments || []).some(p => !channelSet.has(p.channel));
              const selTx = it.selisih_tx;
              return (
                // Klik baris membuka popup detail (poin D brief bos) — meniru
                // pola Acquisitions.jsx. Edit/Hapus & pencatatan Nilai Aktual
                // rinci sekarang tinggal di popup, bukan di sel tabel yang
                // sempit, supaya konsisten dengan halaman detail lain.
                <tr key={it.id} data-testid={`rekon-${it.id}`} onClick={() => openDetail(it)}
                  className="border-b border-slate-100 hover:bg-emerald-50/40 transition-colors cursor-pointer">
                  <td className="py-3 px-3 font-mono text-xs text-slate-500">{idx + 1}</td>
                  <td className="py-3 px-3">
                    <p className="font-medium text-slate-900">{it.nama_barang}</p>
                    {it.imei && <p className="text-xs text-slate-400 font-mono">{it.imei}</p>}
                    {/* Nomor invoice (poin B brief bos 14 Agu) — klik = buka rincian
                        penjualan di TAB BARU. Hanya untuk baris auto dari Sales (baris
                        manual tidak punya dokumen sale untuk dituju) DAN pengguna
                        berhak melihat modul Sales (pola sama Pelanggan.jsx) — supaya
                        tidak menampilkan tautan yang ujungnya 403. */}
                    {it.is_from_sales && it.source_sale_id && (
                      bolehLihatSales ? (
                        <a href={`/sales?id=${encodeURIComponent(it.source_sale_id)}`}
                          target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                          data-testid={`rekon-invoice-link-${it.id}`}
                          className="inline-flex items-center gap-0.5 text-[10px] font-mono text-sky-700 hover:underline">
                          {noInvoice(it.source_sale_id)}
                        </a>
                      ) : (
                        <span className="text-[10px] font-mono text-slate-400" data-testid={`rekon-invoice-${it.id}`}>
                          {noInvoice(it.source_sale_id)}
                        </span>
                      )
                    )}
                  </td>
                  <td className="py-3 px-3 text-right font-mono font-semibold">{idr(it.jumlah)}</td>
                  <td className="py-3 px-3 text-right">
                    <span className="font-mono text-xs" data-testid={`tx-actual-${it.id}`}>
                      {it.actual_value != null ? idr(it.actual_value) : <span className="text-slate-300">— klik baris —</span>}
                    </span>
                  </td>
                  <td className={`py-3 px-3 text-right font-mono text-xs ${selTx == null ? "text-slate-300" : Math.abs(selTx) < 0.5 ? "text-emerald-700 font-semibold" : "text-rose-700 font-bold"}`}>
                    {selTx == null ? "-" : (selTx === 0 ? "0" : idr(selTx))}
                  </td>
                  <td className="py-3 px-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${it.payments?.length > 1 ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-700"}`}>
                      {it.payments?.length > 1 ? <><Split className="w-3 h-3 inline mr-0.5" /></> : null}{methods}
                    </span>
                    {adaChannelLama && (
                      <span title="Salah satu metode di baris ini bukan metode pembayaran aktif saat ini"
                        className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">lama</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-center">
                    {it.status === "VALID" ? (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> VALID
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-xs font-bold inline-flex items-center gap-1" data-testid={`rekon-cek-${it.id}`}>
                        <AlertTriangle className="w-3 h-3" /> CEK
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-slate-700 text-xs">{it.sales || "-"}</td>
                  <td className="py-3 px-3 text-slate-700 text-xs">{it.nama_pelanggan || "-"}</td>
                  <td className="py-3 px-3 text-xs">
                    {/* Baris auto dari Sales: `barang_akuisisi` SELALU kosong (field itu
                        cuma dipakai baris manual) — trade-in-nya ada di `trade_in_items`.
                        Tanpa cabang ini, transaksi trade-in dari Input Penjualan tampil
                        "-" di sini walau sebenarnya ada tukar tambah. */}
                    {it.trade_in_items?.length > 0 ? (
                      <div>
                        <p className="text-violet-700 truncate max-w-[220px]">
                          {it.trade_in_items.map(t => t.nama).filter(Boolean).join(", ") || "Trade-in"}
                        </p>
                        <p className="font-mono text-violet-700">{idr(it.trade_in_total)}</p>
                      </div>
                    ) : it.barang_akuisisi ? (
                      <div>
                        <p className="text-slate-700 truncate max-w-[220px]">{it.barang_akuisisi}</p>
                        {it.harga_akuisisi > 0 && <p className="font-mono text-amber-700">{idr(it.harga_akuisisi)}</p>}
                      </div>
                    ) : "-"}
                  </td>
                  <td className="py-3 px-3 text-right space-x-1 whitespace-nowrap">
                    {it.is_from_sales && (
                      <span title="Auto-imported dari Input Penjualan (read-only) — klik baris untuk detail"
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">
                        <FileText className="w-3 h-3" /> Auto
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={11} className="text-center py-16 text-sm text-slate-500">
                <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                Belum ada transaksi untuk {date}. Klik <b>Input Transaksi</b> untuk mulai.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* DETAIL MODAL — poin D: klik baris untuk mencatat penerimaan aktual
          secara rinci (pola meniru Acquisitions.jsx). Data sudah lengkap di
          `detail` (salinan baris dari `items`), jadi tidak ada GET tambahan. */}
      {detail && (() => {
        // Rincian per-akun (brief bos 14 Agu #1) — dipakai kalau pembayaran
        // campuran (>1 channel) DAN/ATAU ada trade-in. Baris tunggal tanpa
        // trade-in tetap pakai input tunggal lama (tidak berubah sedikit pun).
        const channels = Object.entries(detail.breakdown || {});
        const hasTradeIn = (detail.trade_in_items || []).length > 0;
        const useBreakdown = channels.length > 1 || hasTradeIn;
        const chDraft = chActualDraft[detail.tx_key] || {};
        const tiDraft = tiActualDraft[detail.tx_key] ?? "";
        const totalAktualBreakdown = round2(
          channels.reduce((s, [ch]) => s + (parseFloat(chDraft[ch]) || 0), 0)
          + (parseFloat(tiDraft) || 0)
        );
        const targetPenerimaan = detail.jumlah_penerimaan_ekspektasi ?? detail.jumlah ?? 0;
        const selisihPreview = round2(totalAktualBreakdown - targetPenerimaan);
        return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}
            data-testid="rekon-detail-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <h3 className="font-display font-semibold">Detail Transaksi Rekon</h3>
              </div>
              <button onClick={() => setDetail(null)} data-testid="rekon-detail-close"
                className="p-2 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Barang</p>
                  <p className="font-display text-lg font-bold text-slate-900">{detail.nama_barang}</p>
                  {detail.imei && <p className="text-xs text-slate-400 font-mono mt-0.5">{detail.imei}</p>}
                  {/* Nomor invoice → TAB BARU ke rincian penjualan (poin B brief bos 14 Agu) */}
                  {detail.is_from_sales && detail.source_sale_id && (
                    bolehLihatSales ? (
                      <a href={`/sales?id=${encodeURIComponent(detail.source_sale_id)}`}
                        target="_blank" rel="noreferrer"
                        data-testid={`rekon-detail-invoice-link-${detail.id}`}
                        className="inline-flex items-center gap-1 mt-1 text-xs font-mono text-sky-700 hover:underline">
                        <FileText className="w-3 h-3" /> {noInvoice(detail.source_sale_id)}
                      </a>
                    ) : (
                      <p className="text-xs font-mono text-slate-400 mt-1">{noInvoice(detail.source_sale_id)}</p>
                    )
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Tanggal</p>
                  <p className="font-mono text-sm text-slate-900">{detail.tanggal}</p>
                  <p className="text-xs text-emerald-700 font-medium mt-1">{detail.branch || "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="Jumlah (Ekspektasi)"><span className="font-mono font-semibold">{idr(detail.jumlah)}</span></Field>
                <Field label="Status">
                  {detail.status === "VALID" ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium inline-flex items-center gap-1 w-fit">
                      <CheckCircle2 className="w-3 h-3" /> VALID
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-xs font-bold inline-flex items-center gap-1 w-fit">
                      <AlertTriangle className="w-3 h-3" /> CEK
                    </span>
                  )}
                </Field>
                <Field label="Sales">{detail.sales || "-"}</Field>
                <Field label="Pelanggan">{detail.nama_pelanggan || "-"}</Field>
                <Field label="Leads">{detail.leads || "-"}</Field>
                <Field label="Sumber">
                  {detail.is_from_sales ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold w-fit">
                      <FileText className="w-3 h-3" /> Auto dari Input Penjualan
                    </span>
                  ) : "Input manual Rekon"}
                </Field>
              </div>

              {/* Rincian metode pembayaran per channel */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">Rincian Metode Pembayaran</p>
                <div className="space-y-1">
                  {(detail.payments || []).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">
                        {p.channel}
                        {!channelSet.has(p.channel) && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">channel lama</span>
                        )}
                      </span>
                      <span className="font-mono font-semibold text-slate-900">{idr(p.amount)}</span>
                    </div>
                  ))}
                  {(detail.payments || []).length === 0 && <p className="text-xs text-slate-400">Tidak ada rincian pembayaran</p>}
                </div>
              </div>

              {detail.barang_akuisisi && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Tukar Tambah / Akuisisi</p>
                  <p className="text-sm text-slate-800">{detail.barang_akuisisi}</p>
                  {detail.harga_akuisisi > 0 && <p className="font-mono text-amber-700 text-sm">{idr(detail.harga_akuisisi)}</p>}
                  {detail.peng_akuisisi && <p className="text-xs text-slate-500 mt-1">QC oleh: {detail.peng_akuisisi}</p>}
                </div>
              )}

              {/* Trade-in dari Input Penjualan (poin A brief bos 14 Agu) — beda dari
                  blok `barang_akuisisi` di atas (khusus baris manual): baris auto
                  Sales membawa `trade_in_items`, bisa lebih dari satu unit. */}
              {detail.trade_in_items?.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3" data-testid="rekon-detail-tradein">
                  <p className="text-xs font-semibold text-violet-800 mb-1">Trade-in pada transaksi ini</p>
                  <ul className="text-xs text-violet-900/90 space-y-0.5">
                    {detail.trade_in_items.map((t, i) => (
                      <li key={i}>· {t.nama || "(tanpa nama)"} {t.imei ? `— IMEI ${t.imei}` : ""} — <b>{idr(t.harga)}</b></li>
                    ))}
                  </ul>
                  <p className="text-xs text-violet-700 mt-1">Total trade-in: <b>{idr(detail.trade_in_total)}</b></p>
                </div>
              )}

              {detail.keterangan && (
                <Field label="Keterangan"><span className="text-slate-600 italic">{detail.keterangan}</span></Field>
              )}

              {/* Penerimaan Aktual — inti poin D (+ poin A brief bos 14 Agu): catat/lihat
                  nilai yang BENAR-BENAR diterima untuk transaksi ini, terpisah dari nilai
                  ekspektasi. Pembayaran campuran (>1 channel) dan/atau ada trade-in ->
                  rincian PER AKUN + kolom trade-in (`useBreakdown`); selain itu tetap
                  input tunggal lama (tidak berubah). */}
              <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50/40" data-testid="rekon-detail-penerimaan-aktual">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800 mb-2">Penerimaan Aktual</p>
                {canAdjust ? (
                  useBreakdown ? (
                    <div className="space-y-2">
                      <p className="text-xs text-emerald-900">
                        Isi nilai yang BENAR-BENAR diterima per akun penerimaan.
                        {hasTradeIn && " Trade-in dihitung sebagai penerimaan (bukan tunai) — tanpa ini penerimaan selalu tampak kurang sebesar nilai trade-in."}
                      </p>
                      <div className="space-y-2">
                        {channels.map(([ch, expected]) => (
                          <div key={ch} className="grid grid-cols-[1fr,160px] gap-3 items-end">
                            <F l={`${ch} — ekspektasi ${idr(expected)}`}>
                              <InputRupiah value={chDraft[ch] ?? ""}
                                onChange={n => setChActualDraft(d => ({ ...d, [detail.tx_key]: { ...(d[detail.tx_key] || {}), [ch]: n } }))}
                                testid={`tx-actual-ch-${detail.id}-${ch}`}
                                className={inp + " text-right font-mono"} />
                            </F>
                          </div>
                        ))}
                        {hasTradeIn && (
                          <div className="grid grid-cols-[1fr,160px] gap-3 items-end border-t border-emerald-100 pt-2">
                            <F l={`Penerimaan Trade-in — ekspektasi ${idr(detail.trade_in_total || 0)}`}>
                              <InputRupiah value={tiDraft}
                                onChange={n => setTiActualDraft(d => ({ ...d, [detail.tx_key]: n }))}
                                testid={`tx-actual-tradein-${detail.id}`}
                                className={inp + " text-right font-mono"} />
                            </F>
                          </div>
                        )}
                      </div>
                      <F l="Catatan">
                        <input value={txNoteDraft[detail.tx_key] ?? ""}
                          onChange={e => setTxNoteDraft(n => ({ ...n, [detail.tx_key]: e.target.value }))}
                          data-testid={`tx-actual-note-${detail.id}`}
                          placeholder="opsional — mis. dipotong admin bank"
                          className={inp} />
                      </F>
                      <div className="flex items-center justify-between pt-1">
                        <div className="text-xs">
                          <p className="text-slate-600">Total aktual: <span className="font-mono font-semibold">{idr(totalAktualBreakdown)}</span></p>
                          <p className={`font-medium ${Math.abs(selisihPreview) < 0.5 ? "text-emerald-700" : "text-rose-700"}`} data-testid={`tx-actual-selisih-preview-${detail.id}`}>
                            Selisih vs ekspektasi: {idr(selisihPreview)}
                          </p>
                        </div>
                        <button onClick={() => saveTxActualBreakdown(detail)} data-testid={`btn-save-tx-actual-breakdown-${detail.id}`}
                          className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                          Simpan Rincian Penerimaan
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <F l="Nilai Aktual Diterima (Rp)">
                          <InputRupiah value={txActualDraft[detail.tx_key] ?? ""}
                            onChange={n => setTxActualDraft(d => ({ ...d, [detail.tx_key]: n }))}
                            testid={`tx-actual-input-${detail.id}`}
                            className={inp + " text-right font-mono"} />
                        </F>
                        <F l="Catatan">
                          <input value={txNoteDraft[detail.tx_key] ?? ""}
                            onChange={e => setTxNoteDraft(n => ({ ...n, [detail.tx_key]: e.target.value }))}
                            data-testid={`tx-actual-note-${detail.id}`}
                            placeholder="opsional — mis. dipotong admin bank"
                            className={inp} />
                        </F>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className={`text-xs font-medium ${detail.selisih_tx == null ? "text-slate-400" : Math.abs(detail.selisih_tx) < 0.5 ? "text-emerald-700" : "text-rose-700"}`}>
                          Selisih vs ekspektasi: {detail.selisih_tx == null ? "belum dicatat" : idr(detail.selisih_tx)}
                        </p>
                        <button onClick={() => saveTxActual(detail.tx_key)} data-testid={`btn-save-tx-actual-${detail.id}`}
                          className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                          Simpan Nilai Aktual
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-sm">
                    <p className="font-mono font-semibold">{detail.actual_value != null ? idr(detail.actual_value) : "Belum dicatat"}</p>
                    {detail.actual_note && <p className="text-xs text-slate-500 italic mt-1">{detail.actual_note}</p>}
                    {(detail.channel_actuals || []).length > 0 && (
                      <ul className="text-xs text-slate-500 mt-1 space-y-0.5">
                        {detail.channel_actuals.map((c, i) => <li key={i}>· {c.channel}: {idr(c.actual_value)}</li>)}
                        {hasTradeIn && <li>· Trade-in: {idr(detail.trade_in_actual || 0)}</li>}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                {!detail.is_from_sales && (
                  <>
                    {canDelete && (
                      <button onClick={() => del(detail.id)} data-testid="rekon-detail-delete"
                        className="h-10 px-4 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                        <Trash2 className="w-4 h-4" /> Hapus
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => { const d = detail; setDetail(null); openEdit(d); }} data-testid="rekon-detail-edit"
                        className="h-10 px-4 rounded-lg bg-[#0F172A] hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-1.5">
                        <Pencil className="w-4 h-4" /> Edit
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* MODAL */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={rekonGuard.requestClose}>
          <div ref={rekonGuard.contentRef} className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-emerald-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                <h3 className="font-display font-semibold">{mode === "edit" ? "Edit Transaksi Rekon" : "Input Transaksi Rekon"}</h3>
              </div>
              <button onClick={rekonGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-5">
              {/* Basic info */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <F l="Tanggal"><DatePicker value={form.tanggal} onChange={v => setForm(s => ({ ...s, tanggal: v }))} data-testid="rekon-form-tanggal" /></F>
                <F l="Branch">
                  <select value={form.branch} onChange={e => setForm(s => ({ ...s, branch: e.target.value }))} className={inp + " bg-white"}>
                    {BRANCHES.map(b => <option key={b} value={b}>{b || "— pilih —"}</option>)}
                  </select>
                </F>
                <F l="IMEI (opsional)">
                  <AutocompleteInput value={form.imei}
                    onChange={v => setForm(s => ({ ...s, imei: v }))}
                    field="imei" className={inp + " font-mono"}
                    placeholder="opsional"
                    data-testid="rekon-form-imei" />
                </F>
              </div>

              {/* Item + jumlah */}
              <div className="grid grid-cols-1 sm:grid-cols-[2fr,1fr] gap-3">
                <F l="Nama Barang *">
                  <input value={form.nama_barang} onChange={e => setForm(s => ({ ...s, nama_barang: e.target.value }))} className={inp}
                    placeholder="mis. iPhone 15 Pro 256GB Blue" data-testid="rekon-form-barang" />
                </F>
                <F l="Jumlah (Rp) *">
                  <InputRupiah value={form.jumlah || ""}
                    onChange={n => setForm(s => ({ ...s, jumlah: n }))}
                    testid="rekon-form-jumlah" className={inp + " text-right font-mono font-semibold text-emerald-700"} />
                </F>
              </div>

              {/* Payment mode toggle */}
              <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                    <button type="button" onClick={() => setForm(s => ({ ...s, mode: "single" }))} data-testid="rekon-mode-single"
                      className={`px-4 py-2 text-xs font-medium ${form.mode === "single" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                      <Sparkles className="w-3.5 h-3.5 inline mr-1" /> Sekali Bayar (1 metode)
                    </button>
                    <button type="button" onClick={() => setForm(s => ({ ...s, mode: "split", payments: (s.payments || []).length ? s.payments : [{ channel: s.single_channel || "Cash", amount: s.jumlah || 0 }] }))} data-testid="rekon-mode-split"
                      className={`px-4 py-2 text-xs font-medium ${form.mode === "split" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                      <Split className="w-3.5 h-3.5 inline mr-1" /> Campuran (split)
                    </button>
                  </div>
                </div>

                {form.mode === "single" ? (
                  <div>
                    <p className="text-xs text-emerald-900 mb-2">💡 Pembayaran dengan 1 metode — nominal otomatis = jumlah transaksi.</p>
                    <F l="Metode Pembayaran">
                      <select value={form.single_channel} onChange={e => setForm(s => ({ ...s, single_channel: e.target.value }))}
                        data-testid="rekon-form-channel" className={inp + " bg-white font-semibold"}>
                        {channelOptions.map(c => <option key={c} value={c}>{c}{!channelSet.has(c) ? " (channel lama)" : ""}</option>)}
                      </select>
                    </F>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-emerald-900 mb-2">💡 Bagi jumlah ke beberapa metode. Total split harus = Jumlah.</p>
                    <div className="space-y-2">
                      {(form.payments || []).map((p, i) => (
                        <div key={i} className="grid grid-cols-[1fr,140px,auto] gap-2 items-center">
                          <select value={p.channel} onChange={e => updPay(i, "channel", e.target.value)}
                            data-testid={`rekon-pay-${i}-channel`}
                            className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                            {channelOptions.map(c => <option key={c} value={c}>{c}{!channelSet.has(c) ? " (channel lama)" : ""}</option>)}
                          </select>
                          <InputRupiah placeholder="Nominal" value={p.amount || ""}
                            onChange={n => updPay(i, "amount", n)}
                            testid={`rekon-pay-${i}-amount`}
                            className="h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                          {(form.payments || []).length > 1 && (
                            <button onClick={() => rmPay(i)} className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-1">
                        <button onClick={addPay} data-testid="rekon-add-pay" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                          <Plus className="w-3.5 h-3.5" /> Tambah Metode
                        </button>
                        <div className="text-xs text-right">
                          <p className="text-slate-500">Total: <span className="font-mono font-semibold">{idr(splitTotal)}</span></p>
                          <p className={Math.abs(splitBalance) < 0.5 ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>
                            {Math.abs(splitBalance) < 0.5 ? "✓ Sesuai Jumlah" : `Selisih ${idr(splitBalance)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sales & customer */}
              <div className="grid grid-cols-2 gap-3">
                <F l="Sales">
                  <select value={form.sales} onChange={e => setForm(s => ({ ...s, sales: e.target.value }))} data-testid="rekon-form-sales" className={inp + " bg-white"}>
                    <option value="">— pilih sales —</option>
                    {employees.map(e => <option key={e.id} value={e.nama}>{e.nama}</option>)}
                  </select>
                </F>
                <F l="Nama Pelanggan">
                  <AutocompleteInput value={form.nama_pelanggan}
                    onChange={v => setForm(s => ({ ...s, nama_pelanggan: v }))}
                    field="nama_pelanggan" className={inp}
                    placeholder="Nama pelanggan"
                    data-testid="rekon-form-pelanggan" />
                </F>
                <F l="Sumber Leads">
                  <select value={form.leads} onChange={e => setForm(s => ({ ...s, leads: e.target.value }))} data-testid="rekon-form-leads" className={inp + " bg-white"}>
                    {LEADS.map(l => <option key={l}>{l}</option>)}
                  </select>
                </F>
                <F l="Keterangan"><input value={form.keterangan} onChange={e => setForm(s => ({ ...s, keterangan: e.target.value }))} className={inp} placeholder="opsional" /></F>
              </div>

              {/* Trade-in section */}
              <details className="border border-slate-200 rounded-lg p-3 bg-slate-50/40" open={!!form.barang_akuisisi}>
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Tukar Tambah / Akuisisi Barang (opsional)
                </summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <F l="Barang Akuisisi">
                    <AutocompleteInput value={form.barang_akuisisi}
                      onChange={v => setForm(s => ({ ...s, barang_akuisisi: v }))}
                      field="barang_akuisisi" className={inp}
                      placeholder="mis. iPhone 12 128GB BH 87%"
                      data-testid="rekon-form-tradein" />
                  </F>
                  <F l="Harga Akuisisi (Rp)"><InputRupiah value={form.harga_akuisisi || ""} onChange={n => setForm(s => ({ ...s, harga_akuisisi: n }))} className={inp + " text-right font-mono"} /></F>
                  <F l="Peng-Akuisisi (yang QC)">
                    <select value={form.peng_akuisisi} onChange={e => setForm(s => ({ ...s, peng_akuisisi: e.target.value }))} className={inp + " bg-white"}>
                      <option value="">— tidak set —</option>
                      {employees.map(e => <option key={e.id} value={e.nama}>{e.nama}</option>)}
                    </select>
                  </F>
                </div>
              </details>

              {/* Live preview status */}
              <PreviewStatus form={form} splitBalance={splitBalance} />

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={rekonGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-rekon" disabled={saving}
                  className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                  {mode === "edit" ? <Pencil className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  {saving ? "Menyimpan..." : (mode === "edit" ? "Simpan Perubahan" : "Simpan Transaksi")}
                </button>
              </div>
              {rekonGuard.confirming && (
                <UnsavedGuardBar onStay={rekonGuard.cancelClose} onDiscard={rekonGuard.confirmDiscard} testid="rekon-modal-guard-bar" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewStatus({ form, splitBalance }) {
  let selisih = 0;
  if (form.mode === "single") {
    selisih = 0;
  } else {
    selisih = splitBalance;
  }
  const isValid = Math.abs(selisih) < 0.5;
  return (
    <div className={`rounded-lg p-3 border ${isValid ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
      <p className={`text-xs font-semibold flex items-center gap-1.5 ${isValid ? "text-emerald-800" : "text-rose-800"}`}>
        {isValid ? <><CheckCircle2 className="w-4 h-4" /> Status akan: VALID</> : <><AlertTriangle className="w-4 h-4" /> Status akan: CEK — selisih {idr(selisih)}</>}
      </p>
    </div>
  );
}

const inp = "h-10 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-600/40";
function F({ l, children }) {
  return <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">{l}</span>{children}</label>;
}
// Read-only label+value pair — dipakai Detail Modal (pola sama dengan
// `Field` di Acquisitions.jsx), beda dari `F` di atas yang membungkus INPUT.
function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
      <div className="text-slate-900 mt-0.5">{children}</div>
    </div>
  );
}
